<template>
    <tailwind-layout title="Create Card">
        <div class="">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-rent-risk-blue-100 overflow-hidden shadow-xl sm:rounded-lg">
                    <div class="bg-white pt-8 pb-16 px-4 overflow-hidden sm:px-6 lg:px-8 lg:py-8">
                        <div class="relative max-w-xl mx-auto">
                            <svg class="absolute left-full transform translate-x-1/2" width="404" height="404" fill="none" viewBox="0 0 404 404" aria-hidden="true">
                                <defs>
                                    <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                        <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                                    </pattern>
                                </defs>
                                <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
                            </svg>
                            <svg class="absolute right-full bottom-0 transform -translate-x-1/2" width="404" height="404" fill="none" viewBox="0 0 404 404" aria-hidden="true">
                                <defs>
                                    <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                        <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                                    </pattern>
                                </defs>
                                <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
                            </svg>
                            <div class="text-center">
                                <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                                    Card Information
                                </h2>
                            </div>
                            <div class="mt-6">
                                <alert-card :type="'info'" v-if="landlord_property_edit_url">
                                    <p class="">
                                        A credit card is required because you've selected options that involve charges assessed per application, such as paying the application fee for the applicant or requesting enhanced tenant screening.
                                    </p>
                                    <p class="">
                                        If you’d like to change your selections, you can <inertia-link :href="landlord_property_edit_url" class="underline text-blue-600">go back</inertia-link> and change this option.
                                    </p>
                                </alert-card>
                                <alert-card :type="'info'" v-else>
                                    <p class="text-gray-700 leading-relaxed">
                                        This card will be used in the following cases if you choose to opt-in:
                                    </p>
                                    <ul class="list-disc list-inside text-gray-700 space-y-1">
                                        <li>Paying application fees on behalf of rental applicants.</li>
                                        <li>Enhanced Screening options to verify an applicant's data.</li>
                                        <li>Rent payment subscription charges.</li>
                                        <li>Maintenance subscription charges.</li>
                                        <li>RiskGuard subscription charges.</li>
                                    </ul>
                                </alert-card>
                                <form @submit.prevent="store" class="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-8">
                                    <div class="sm:col-span-2">
                                        <div class="flex justify-between space-x-4 mt-4">
                                            <div class="w-1/2">
                                                <label for="first_name" class="block text-sm font-medium text-gray-700">First name</label>
                                                <div class="mt-1">
                                                    <input type="text" v-model="form.first_name" :error="form.errors.first_name" id="first_name" autocomplete="given_name" v-bind:class="(form.errors.first_name)?'py-3 px-4 block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-red-500 rounded-md':'py-3 px-4 block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-gray-300 rounded-md'">
                                                    <p v-text="form.errors.first_name" class="text-red-500"></p>
                                                </div>
                                            </div>
                                            <div class="w-1/2">
                                                <label for="last_name" class="block text-sm font-medium text-gray-700">Last name</label>
                                                <div class="mt-1">
                                                    <input type="text" v-model="form.last_name" :error="form.errors.last_name" id="last_name" autocomplete="family-name" v-bind:class="(form.errors.last_name)?'py-3 px-4 block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-red-500 rounded-md':'py-3 px-4 block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-gray-300 rounded-md'">
                                                    <p v-text="form.errors.last_name" class="text-red-500"></p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="sm:col-span-2">
                                        <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                                        <div class="mt-1">
                                            <input id="email" v-model="form.email" :error="form.errors.email" type="email" autocomplete="email" v-bind:class="(form.errors.email)?'py-3 px-4 block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-red-500 rounded-md':'py-3 px-4 block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-gray-300 rounded-md'">
                                            <p v-text="form.errors.email" class="text-red-500"></p>
                                        </div>
                                    </div>
                                    <div class="sm:col-span-2">
                                        <div id="payment-element" class="mt-4"></div>
                                        <div id="error-message" class="bg-red-600 text-red-200 p-4 rounded-lg mt-4 hidden"></div>
                                    </div>
                                    <div class="sm:col-span-2 mt-4">
                                        <form-button id="purchase-btn" :form="form" :disabled="!isButtonActive" :data-secret="setup_intent.client_secret" class="-ml-0 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-rent-risk-blue-400 hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 disabled:bg-indigo-100" type="submit">
                                            Add Card
                                        </form-button>
                                    </div>
                                    <div class="sm:col-span-2 text-center">
                                        <span class="text-xs tracking-tight text-gray-500">
                                            By clicking Add Card, you agree to our <a :href="route('privacy-policy')" target="_blank" class="underline">Privacy Policy</a> and <a :href="route('terms-and-conditions')" target="_blank" class="underline">Terms and Conditions</a>.
                                        </span>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import AlertCard from "@/Shared/AlertCard.vue";
import axios from "axios";

export default {
    props: {
        publishable_key: String,
        first_name: String,
        last_name: String,
        user: Object,
        setup_intent: Object,
        landlord_property_edit_url: String,
    },
    mounted(){
        this.includeStripe('js.stripe.com/v3/', function(){
            this.configureStripe();
        }.bind(this) );
    },
    setup() {
        const user = computed(() => usePage().props.user);
        return {user};
    },
    components: {
        TailwindLayout,
        FormButton,
        AlertCard,
    },
    remember: 'form',
    data() {
        return {
            button_active_state: false,
            stripeAPIToken: this.publishable_key,
            stripe: '',
            elements: '',
            paymentElement: '',
            card: '',
            form: this.$inertia.form({
                first_name: this.first_name,
                last_name: this.last_name,
                email: this.user.email,
                card: null,
                setup_intent: null,
                token: null,
            }),
            isButtonActive: true,
            style: {
                base: {
                    lineHeight: 3.5,
                    padding: 20,
                    backgroundColor: '#fff',
                    border: '2px solid #e0dddd',
                },
                invalid: {

                },
            },
        }
    },
    methods: {
        includeStripe( URL, callback ){
            let documentTag = document, tag = 'script',
                object = documentTag.createElement(tag),
                scriptTag = documentTag.getElementsByTagName(tag)[0];
            object.src = '//' + URL;
            if (callback) { object.addEventListener('load', function (e) { callback(null, e); }, false); }
            scriptTag.parentNode.insertBefore(object, scriptTag);
        },

        /*
            Configures Stripe by setting up the elements and
            creating the card element.
        */
        configureStripe() {
            this.stripe = Stripe(this.stripeAPIToken);
            const options = {
                clientSecret: this.setup_intent.client_secret,
                appearance: this.style, // Use the same appearance object as the second form
            };
            this.elements = this.stripe.elements(options);

            // 86bckgczc: Apple Pay / Google Pay stay on; Link is off, matching StripeForm.
            this.paymentElement = this.elements.create('payment', {
                wallets: { link: 'never' },
                defaultValues: {
                    billingDetails: {
                        name: this.user.name,
                        email: this.user.email,
                    }
                }
            });

            this.paymentElement.mount('#payment-element');
        },
        async store() {
            if (!this.form.first_name || !this.form.last_name || !this.form.email) {
                this.form.errors.first_name = !this.form.first_name ? 'The first name is required.' : null;
                this.form.errors.last_name = !this.form.last_name ? 'The last name is required.' : null;
                this.form.errors.email = !this.form.email ? 'The email is required.' : null;
                return false;
            }

            this.isButtonActive = false;

            try {
                const { setupIntent, error } = await this.stripe.confirmSetup({
                    elements: this.elements,
                    confirmParams: {
                        return_url: route('customer-payment-method-created'),
                    },
                    redirect: 'if_required',
                });

                if (error) {
                    this.isButtonActive = true;
                    const messageContainer = document.querySelector('#error-message');
                    if (messageContainer) {
                        messageContainer.classList.remove("hidden");
                        messageContainer.textContent = error.message;
                    }
                    return;
                }

                if (setupIntent?.status === 'succeeded') {
                    await this.persistPaymentMethod(setupIntent.id);
                }
            } catch (error) {
                this.isButtonActive = true;
                console.error("Unexpected error:", error);
            }
        },

        async persistPaymentMethod(setupIntentId) {
            try {
                const response = await axios.post(route('customer-payment-method-created'), {
                    setup_intent: setupIntentId,
                    request_type: 'axios',
                });

                if (response.data.status) {
                    this.$inertia.visit(route('payment-methods.index'), {
                        preserveScroll: true,
                    });
                    return;
                }

                this.isButtonActive = true;
                const messageContainer = document.querySelector('#error-message');
                if (messageContainer) {
                    messageContainer.classList.remove("hidden");
                    messageContainer.textContent = response.data.message || 'Failed to add card. Please try again.';
                }
            } catch (error) {
                this.isButtonActive = true;
                const messageContainer = document.querySelector('#error-message');
                if (messageContainer) {
                    messageContainer.classList.remove("hidden");
                    messageContainer.textContent = error.response?.data?.message || 'Failed to add card. Please try again.';
                }
            }
        }

    }
}
</script>
